import {activateCallWork,clearCallWork,callJobs,drainCallJobs} from '@/lib/callWork';
import { createContext, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { setPreview, appStorage } from "@/lib/employeePreview";
import { releaseCallPrefetch } from "@/lib/callPrefetch";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=anon, object=user
  const [ready, setReady] = useState(false);
  const [showContactData, setShowContactDataState] = useState(() => {
    const v = appStorage.getItem("ls_show_contact");
    return v === null ? true : v === "1";
  });

  useEffect(() => {
    if (user && user.preferences && appStorage.getItem("ls_show_contact") === null) {
      const scd = user.preferences.show_contact_data;
      if (typeof scd === "boolean") setShowContactDataState(scd);
    }
  }, [user]);

  useEffect(() => {
    activateCallWork(user?.id);
    const resume = () => { if (user?.id) void drainCallJobs(user.id, true); };
    resume();
    window.addEventListener("online", resume);
    const warn = event => { if (callJobs(user?.id).length) { event.preventDefault(); event.returnValue = ''; } };
    window.addEventListener('beforeunload', warn);
    return () => { window.removeEventListener('beforeunload', warn); window.removeEventListener('online', resume); };
  }, [user?.id]);

  // Umschalten ist rein UI-seitig (keine Neuladung der Listen); Präferenz wird nur gespeichert.
  const setShowContactData = (v) => {
    setShowContactDataState(v);
    appStorage.setItem("ls_show_contact", v ? "1" : "0");
    api.patch("/auth/me/preferences", { show_contact_data: v }).catch(() => {});
  };

  useEffect(() => {
    localStorage.removeItem("ls_token"); // retire legacy persistent bearer tokens
    const boot = async () => {
      setPreview(null);
      sessionStorage.removeItem("ls_preview_fresh");
      return api.get("/auth/me");
    };
    boot()
      .then((r) => setUser(r.data))
      .catch(() => {
        localStorage.removeItem("ls_token");
        setUser(false);
      })
      .finally(() => setReady(true));
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    return data;
  };

  const logout = async () => {
    clearCallWork(user?.id);
    await releaseCallPrefetch(user?.id);
    try { await api.post("/auth/logout"); } catch (error) {
      toast.error("Abmeldung nicht bestätigt. Bitte erneut versuchen.");
      throw error;
    }
    localStorage.removeItem("ls_token");
    setUser(false);
  };

  const completeLogin = (nextUser) => setUser(nextUser);

  const enterAdminMode = async (password, code) => {
    const { data } = await api.post("/auth/admin-mode/enter", { password, code });
    setUser(data.user);
    return data.user;
  };

  const exitAdminMode = async () => {
    const { data } = await api.post("/auth/admin-mode/exit");
    setUser(data.user);
    return data.user;
  };

  return (
    <AuthContext.Provider value={{ user, ready, login, completeLogin, logout, enterAdminMode, exitAdminMode, apiError, showContactData, setShowContactData }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
